import { ColDef, GridApi, GridOptions, GridReadyEvent, IRowNode } from "ag-grid-community";
import { action, makeObservable, observable } from "mobx";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import PermissionsContext from "../../../../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../../../../contexts/permissions/PermissionsTypes";
import { EntityTypes, GridTypes } from "../../../../../../enums";
import { getEntityNameMicroFormFields } from "../../../../../../pages/change/forms/microForm/getEntityNameMicroFormFields";
import { SingleFormModel } from "../../../../../../pages/change/forms/singleFormModel/SingleForm_model";
import PhasesApi, { PhasesApi as IPhasesApi } from "../../../../../../services/api/v2/phases/Phases.api";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { IGridToastService } from "../../../../../../services/local/gridToastService/IGridToastService";
import I18n from "../../../../../localization/I18n";
import ToasterService, { IToasterService } from "../../../../../toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../toaster/Toaster_model";
import {
  AuccaColDefFieldNamesEnum,
  CommonColDefFieldNamesEnum,
  PhaseColDefFieldNamesEnum
} from "../../../enums/AgGridColDefFieldNameEnum";
import { GridModalBuilder } from "../../modals/GridModal_builder";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import { PhaseField } from "../../utils/GridFields";
import { IGridUiAction } from "../base/AppGridToolbarActions_view";
import { AppGridState } from "../base/AppGrid_view";
import { ColumnStateAddon } from "../base/addons/ColumnStateAddon";
import { FilterStoreAddon } from "../base/addons/FilterAddon/FilterStoreAddon";
import { TextWrapperAddon } from "../base/addons/TextWrapAddon";
import { GetPhasesGridActions } from "./PhasesGridView_actions";
import { PhaseGridColumnBuilder } from "./PhasesGridView_columns";
import { SHOW_CONFIRM_CREATION_MODAL, SHOW_PHASE_DELETE_CONFIRM_MODAL } from "./PhasesGridView_modals";

export class PhaseGridModel {
  organisationId: number;
  projectId: number;
  authUser: FP.Entities.IUser;
  @observable isLoading: boolean = true;
  userCanViewPhases: boolean;
  userCanEditPhases: boolean;
  phasesGridColumnBuilder: PhaseGridColumnBuilder;
  @observable selectedItems: number[] = [];
  @observable.ref gridActions: IGridUiAction[] = [];
  httpProgress: IProgressIndicatorModel;
  phaseProvider: IPhasesApi;
  refreshDataHandler: any;
  gridModalBuilder: GridModalBuilder;
  @observable filterStoreAddon: FilterStoreAddon;
  @observable columnStateAddon: ColumnStateAddon;
  @observable textWrapAddon: TextWrapperAddon;
  gridApi: GridApi;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  @observable gridColumns: ColDef<any, any>[];
  onFieldUpdate: () => void;
  gridToolbarType: AppGridToolbarType;
  @observable preSelectedItemIds: number[];
  gridType: EntityTypes | GridTypes;
  urlSearchParams: URLSearchParams;
  microPhaseForm: SingleFormModel;
  toasterService: IToasterService;
  gridToastService: IGridToastService;
  @observable gridOptions: GridOptions;
  showAudiences: boolean;

  constructor(
    organisationId: number,
    projectId: number,
    authUser: FP.Entities.IUser,
    refreshDataHandler: any,
    onFieldUpdate: () => void,
    gridToolbarType: AppGridToolbarType,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    urlSearchParams: URLSearchParams,
    showAudiences: boolean
  ) {
    makeObservable(this);
    this.userCanViewPhases = PermissionsContext.canViewField(PermissionFields.PROJECTS, organisationId, projectId);
    this.userCanEditPhases = PermissionsContext.canEditField(PermissionFields.PROJECTS, organisationId, projectId);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.authUser = authUser;
    this.showAudiences = showAudiences;
    this.httpProgress = ProgressIndicatorModel;
    this.phaseProvider = PhasesApi;
    this.refreshDataHandler = refreshDataHandler;
    this.onFieldUpdate = onFieldUpdate;
    this.setMicroAddForm();
    this.toasterService = ToasterService;
    this.gridToastService = GridToastService;
    this.gridModalBuilder = new GridModalBuilder();
    this.gridToolbarType = gridToolbarType;
    this.preSelectedItemIds = preSelectedItemIds;
    this.gridType = gridType;
    this.urlSearchParams = urlSearchParams;
    this.generateAddons();
  }

  @action
  onMount = () => {
    this.generateFn();
    this.setIsLoading(false);
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };

  onUnmount = () => {};

  @action
  generateGridConfig = () => {
    this.phasesGridColumnBuilder = new PhaseGridColumnBuilder({
      canEdit: this.userCanEditPhases && this.gridToolbarType !== "link-modal",
      organisationId: this.organisationId,
      projectId: this.projectId,
      userCanViewPhases: this.userCanViewPhases,
      onFieldUpdate: this.onFieldUpdate,
      onRowExpanded: this.onRowExpanded,
      showAudiences: this.showAudiences,
      columns: [
        CommonColDefFieldNamesEnum.Name,
        CommonColDefFieldNamesEnum.Description,
        CommonColDefFieldNamesEnum.StartDate,
        PhaseColDefFieldNamesEnum.TotalPulses,
        CommonColDefFieldNamesEnum.ProgressStatus,
        AuccaColDefFieldNamesEnum.Awareness,
        AuccaColDefFieldNamesEnum.Understanding,
        AuccaColDefFieldNamesEnum.Commitment,
        AuccaColDefFieldNamesEnum.Capability,
        AuccaColDefFieldNamesEnum.Adoption,
        CommonColDefFieldNamesEnum.CreatedBy,
        CommonColDefFieldNamesEnum.CreatedAt,
        CommonColDefFieldNamesEnum.ModifiedBy,
        CommonColDefFieldNamesEnum.UpdatedAt
      ]
    });
    this.gridColumns = this.phasesGridColumnBuilder.generateColumnDefs();
  };

  generateActions = () => {
    this.setGridActions(GetPhasesGridActions(this));
  };

  @action
  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(this.projectId, this.gridType ?? EntityTypes.PHASES);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: this.projectId,
      gridType: this.gridType ?? EntityTypes.PHASES,
      filterHasChangedFn: this.setIsFilterChanged,
      urlSearchParams: this.urlSearchParams
    });
    this.columnStateAddon = new ColumnStateAddon({
      projectId: this.projectId,
      gridType: this.gridType ?? EntityTypes.PHASES,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  onRowExpanded = () => {};

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  setGridOptions = (gridOptions: GridOptions) => {
    this.gridOptions = gridOptions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };

  @action
  onFirstDataRendered = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.preSelectedItemIds?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  @action
  onGridReady = (gridReadyEvent: GridReadyEvent) => {
    this.gridApi = gridReadyEvent.api;
  };

  @action
  setMicroAddForm = () => {
    this.microPhaseForm = new SingleFormModel();
    this.microPhaseForm.formFields = getEntityNameMicroFormFields(
      this.createMicroPhase,
      I18n.t("placeholders.myNewName", { entity: I18n.t("entities.phases") })
    );
  };

  @action
  deleteFieldData = async (phaseId: number, phaseField: PhaseField) => {
    const res = await this.phaseProvider.deleteGridField(this.organisationId, this.projectId, phaseId, phaseField);
    if (res.isError) return false;
    if (this.refreshDataHandler) {
      await this.refreshDataHandler();
    }
    this.gridToastService.showToast(res.code, res.message);

    return true;
  };

  @action
  createMicroPhase = async () => {
    let phaseFormRes = await this.microPhaseForm.submit();
    this.microPhaseForm.isSaving = true;
    if (!phaseFormRes) return;

    phaseFormRes = {
      ...phaseFormRes,
      projectId: this.projectId
    };

    this.httpProgress.showOverlay();
    let phaseExists = await this.phaseProvider.getByProjectIdAndName(
      this.organisationId,
      this.projectId,
      phaseFormRes.name
    );

    if (phaseExists && !phaseExists.isError && phaseExists.payload.length) {
      this.httpProgress.hideOverlay();
      let confirmCreatePhase = await this.confirmCreatePhase(phaseFormRes.name);
      if (!confirmCreatePhase) return;
      this.httpProgress.showOverlay();
    }

    const res = {
      ...phaseFormRes
    };

    this.httpProgress.showOverlay();
    const result = await this.phaseProvider.createProjectPhase(
      this.organisationId,
      this.projectId,
      res as FP.Entities.IPhase
    );
    await this.refreshDataHandler();
    this.httpProgress.hideOverlay();

    if (!result || result.isError) return;

    const phase = result.payload;
    this.microPhaseForm.resetFields();
    this.toasterService
      .showSuccessToast()
      .setContent(<span>{I18n.t("phrases.itemCreatedSuccessfully", { item: I18n.t("entities.phase") })}</span>)
      .startTimer(TOASTER_TOAST_TIME.NORMAL);

    return phase;
  };

  confirmCreatePhase = async (name: string): Promise<boolean> => {
    return SHOW_CONFIRM_CREATION_MODAL(name);
  };

  showPhaseConfirmDeleteModal = () => {
    return SHOW_PHASE_DELETE_CONFIRM_MODAL(this.selectedItems, this.removeItems);
  };

  removeItems = async (itemIds: number[]) => {
    this.httpProgress.showOverlay();
    let res = await this.phaseProvider
      .deleteRange(this.organisationId, this.projectId, itemIds)
      .then(e => this.refreshDataHandler && this.refreshDataHandler());
    this.httpProgress.hideOverlay();
    if (!res || res.isError) return;
    return res.payload;
  };

  handleAddPulseClick = () => {};

  handleViewPulsesClick = () => {};
}
